/**
 * Configuration for retry behavior with exponential backoff
 */
export type RetryConfig = {
  /** Maximum number of retry attempts (default: 5) */
  maxRetries?: number;
  /** Initial delay in milliseconds (default: 100) */
  initialDelayMs?: number;
  /** Maximum delay between retries in milliseconds (default: 32000) */
  maxDelayMs?: number;
  /** Maximum total time to spend retrying in milliseconds (default: 5 minutes) */
  maxTotalTimeoutMs?: number;
};

const DEFAULT_RETRY_CONFIG: Required<RetryConfig> = {
  maxRetries: 20,
  initialDelayMs: 500,
  maxDelayMs: 30000,
  maxTotalTimeoutMs: 10 * 60 * 1000, // 10 minutes
};

/**
 * Custom error class for transient errors
 */
export class TransientError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number | null = null,
    public readonly originalError: Error | null = null
  ) {
    super(message);
    this.name = "TransientError";
  }
}

/**
 * Determines if an error is transient (should retry) or permanent (should not)
 * Transient errors: network failures, timeouts, server errors (5xx), rate limits (429)
 * Permanent errors: bad request (4xx except 408/429), auth failures, etc.
 */
export function isTransientError(statusCode: number | null, error: Error): boolean {
  // Network errors are transient
  if (error instanceof TypeError) {
    return true;
  }

  // HTTP status codes that are transient
  const transientStatuses = [
    408, // Request Timeout
    429, // Too Many Requests
    500, // Internal Server Error
    502, // Bad Gateway
    503, // Service Unavailable
    504, // Gateway Timeout
  ];

  return statusCode !== null && transientStatuses.includes(statusCode);
}

/**
 * Waits for the browser to come online with optional timeout
 */
export async function waitForOnline(timeoutMs: number = 30000): Promise<boolean> {
  if (navigator.onLine) {
    return true;
  }

  return new Promise((resolve) => {
    const handleOnline = () => {
      cleanup();
      resolve(true);
    };

    const timeout = setTimeout(() => {
      cleanup();
      resolve(false);
    }, timeoutMs);

    const cleanup = () => {
      window.removeEventListener("online", handleOnline);
      clearTimeout(timeout);
    };

    window.addEventListener("online", handleOnline);
  });
}

/**
 * Wrapper around fetch that automatically converts network errors to TransientError
 * This allows the retry mechanism to properly handle offline/network failures
 */
export async function fetchWithNetworkHandling(
  url: string,
  options?: RequestInit
): Promise<Response> {
  try {
    return await fetch(url, options);
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    // Convert network errors to TransientError for retry handling
    if (err instanceof TypeError) {
      throw new TransientError(`Network error: ${err.message}`, null, err);
    }
    throw err;
  }
}

/**
 * Helper to check HTTP response and throw appropriate error
 */
export function checkResponseOk(response: Response, errorPrefix: string): void {
  if (!response.ok) {
    const errorMsg = `HTTP ${response.status} ${response.statusText}`;
    if (isTransientError(response.status, new Error())) {
      throw new TransientError(errorMsg, response.status);
    }
    throw new Error(`${errorPrefix}: ${errorMsg}`);
  }
}

/**
 * Executes an async operation with exponential backoff retry logic.
 * Automatically detects transient vs permanent errors and only retries transient ones.
 * Checks online status and waits for network to restore if needed.
 */
export async function executeWithRetry<T>(
  operation: () => Promise<T>,
  operationName: string,
  config: RetryConfig = {}
): Promise<T> {
  const fullConfig = { ...DEFAULT_RETRY_CONFIG, ...config };
  const startTime = Date.now();
  let lastError: Error | null = null;
  let delayMs = fullConfig.initialDelayMs;

  for (let attempt = 0; attempt <= fullConfig.maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      lastError = err;
     
      if (!(err instanceof TransientError)) {
        throw err; // Don't retry permanent errors
      }

      // Check if we've exceeded total timeout
      const elapsed = Date.now() - startTime;
      if (elapsed + delayMs > fullConfig.maxTotalTimeoutMs) {
        throw new Error(
          `${operationName} failed after ${elapsed}ms (max timeout: ${fullConfig.maxTotalTimeoutMs}ms): ${err.message}`
        );
      }

      // Last attempt, don't wait
      if (attempt === fullConfig.maxRetries) {
        throw new Error(
          `${operationName} failed after ${fullConfig.maxRetries} retries: ${err.message}`
        );
      }

      console.warn(
        `${operationName} attempt ${attempt + 1}/${fullConfig.maxRetries + 1} failed: ${err.message}. Retrying in ${delayMs}ms...`
      );

      // Wait for network if offline
      if (!navigator.onLine) {
        console.log(`${operationName}: offline - waiting for network connection...`);
        const online = await waitForOnline(delayMs);
        if (!online) {
          console.warn(`${operationName}: network timeout after ${delayMs}ms`);
        }
      } else {
        // Standard exponential backoff delay
        await new Promise((resolve) => setTimeout(resolve, delayMs));
      }

      // Exponential backoff: double the delay, up to max
      delayMs = Math.min(Math.floor(delayMs * 2), fullConfig.maxDelayMs);
    }
  }

  throw new Error(
    `${operationName} failed after exhausting retries: ${lastError?.message || "Unknown error"}`
  );
}